import { type ApolloError, useQuery } from "@apollo/client";
import { useMemo } from "react";

import type { GetYourCasesQuery } from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../../lib/apollo/utils";
import { useAuth } from "../../../lib/auth";
import { bandCases, countBlockedCases } from "./caseSignals";
import { getYourCasesQuery } from "./queries";

// "Your cases" shows the most recent cases across both bands; overflow spills to
// the /cases open list via "view all".
export const MAX_HOME_CASES = 5;

type QueryIssue = NonNullable<
  NonNullable<GetYourCasesQuery["me"]>["issues"]
>[number];

export type YourCase = QueryIssue;

const recencyOf = (caseItem: YourCase): number => {
  const date = caseItem.lastEvent?.sentAt ?? caseItem.openedAt;
  return date ? Date.parse(date) : Number.NEGATIVE_INFINITY;
};

const byRecencyDesc = (a: YourCase, b: YourCase): number =>
  recencyOf(b) - recencyOf(a);

export type UseYourCasesResult = {
  /** Capped, banded (action-required first), recency-sorted cases for the list. */
  cases: YourCase[];
  /** All open cases blocked on the user — feeds the attention alert. */
  blockedCases: YourCase[];
  /** Count of all blocked cases (not just the visible ones). */
  blockedCount: number;
  /** Total open cases, used to decide whether "view all" shows. */
  totalCount: number;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<unknown>;
};

export function useYourCases(): UseYourCasesResult {
  const { isAuthenticated, isReady } = useAuth();

  const { data, loading, error, refetch } = useQuery(getYourCasesQuery, {
    context: errorHandling("caller"),
    skip: !isAuthenticated || !isReady,
  });

  return useMemo(() => {
    const allCases = data?.me?.issues ?? [];
    const { actionRequired, inProgress } = bandCases(allCases);
    // Blocked band on top, in-progress after; recency within each band.
    const blockedByRecency = [...actionRequired].sort(byRecencyDesc);
    const ordered = [
      ...blockedByRecency,
      ...[...inProgress].sort(byRecencyDesc),
    ];

    return {
      cases: ordered.slice(0, MAX_HOME_CASES),
      blockedCases: blockedByRecency,
      blockedCount: countBlockedCases(allCases),
      totalCount: allCases.length,
      loading,
      error,
      refetch,
    };
  }, [data, loading, error, refetch]);
}
